import { useQueryClient } from '@tanstack/react-query'
import { cta_buttonPrimaryText } from 'ext-ui/style/colors'
import AiStarsIconV2 from 'ext-ui/svg/icon/AiStarsIconV2'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom'
import type {
  AskReportingChartQuestion,
  ReportingChartQuestionRequest,
} from 'src/agent/reporting/chartQuestion.types'
import type { ReportingAccessDeniedEvent } from 'src/agent/reporting/conversation/reportingConversation.types'
import type {
  PanelGatedState,
  ReportingDateRange,
} from 'src/app/Reporting/components/reportingPanelUtils'
import {
  createDateRangeFromSearch,
  createDefaultDateRange,
} from 'src/app/Reporting/components/reportingPanelUtils'
import Reporting from 'src/app/Reporting/Reporting'
import ReportingAgentFlyout from 'src/component-flyout/ReportingAgentFlyout/ReportingAgentFlyout'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { SubnavDesign, SubnavOption } from 'src/components/Subnav/Subnav'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { useFlyoutRoute } from 'src/navigation/modalParams'
import { reportingRouteTree } from 'src/navigation/reportingRouteTree'
import {
  departmentsUrl,
  organizationIntegrationsUrl,
  reportingPinnedUrl,
  upgradeSubscriptionUrl,
} from 'src/navigation/urls'
import {
  ReportingAccessProvider,
  useReportingAccess,
} from 'src/page-components/ReportingFamilyPage/reportingAccess'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { scrubReportingPrivateCache } from 'src/queries/reporting/reporting.cache'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  isReportingAgentEnabled,
  isSpendReportingDashboardEnabled,
} from 'src/utils/featureFlags'

import ReportingPinnedPage from './ReportingPinnedPage'

const newDraftRequestId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`

interface ReportingAccessGateProps {
  children: React.ReactNode
  onAccessDenied?: () => void
}

const ReportingAccessGate: React.FC<ReportingAccessGateProps> = ({
  children,
  onAccessDenied,
}) => {
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const { data: user, isLoading: isUserLoading } = useQueryOwnUser({})
  const enabled = isSpendReportingDashboardEnabled(
    remoteConfig,
    user?.organizationId,
  )
  const { data: features, isLoading: isFeatureLoading } = useQueryUserFeatures(
    [UserFeature.VIEW_REPORTING],
    { enabled: enabled && Boolean(user?.organizationId) },
  )
  const isAccessLoading = isUserLoading || (enabled && isFeatureLoading)
  const hasAccess = enabled && features?.VIEW_REPORTING === true

  useEffect(() => {
    if (!isAccessLoading && !hasAccess) onAccessDenied?.()
  }, [hasAccess, isAccessLoading, onAccessDenied])

  if (isAccessLoading) return <LoadingBackgroundPage />
  if (!hasAccess) return <main data-testid="reporting-unavailable" />
  return (
    <ReportingAccessProvider value={hasAccess}>
      {children}
    </ReportingAccessProvider>
  )
}

interface ReportingPrivateScope {
  actorId?: string
  organizationId?: string
  reportingAgentEnabled: boolean
  reportingEnabled: boolean
}

const samePrivateScope = (
  left: ReportingPrivateScope,
  right: ReportingPrivateScope,
): boolean =>
  left.actorId === right.actorId &&
  left.organizationId === right.organizationId &&
  left.reportingAgentEnabled === right.reportingAgentEnabled &&
  left.reportingEnabled === right.reportingEnabled

/**
 * Keep the private reporting cache guard at the route-family boundary so an
 * actor, organization, or feature transition clears both conversation and
 * live-report transports before the next route reads them.
 */
interface ReportingPrivateCacheBoundaryProps {
  reportingAgentEnabled: boolean
}

const ReportingPrivateCacheBoundary: React.FC<
  ReportingPrivateCacheBoundaryProps
> = ({ reportingAgentEnabled }) => {
  const queryClient = useQueryClient()
  const { data: user } = useQueryOwnUser({})
  const reportingEnabled = useReportingAccess()
  const scope = {
    actorId: user?.id,
    organizationId: user?.organizationId,
    reportingAgentEnabled,
    reportingEnabled,
  }
  const scopeRef = React.useRef(scope)

  useEffect(() => {
    const previous = scopeRef.current
    if (!samePrivateScope(previous, scope)) {
      scrubReportingPrivateCache(
        queryClient,
        previous.actorId,
        previous.organizationId,
      )
    }
    scopeRef.current = scope
  }, [
    queryClient,
    scope.actorId,
    scope.organizationId,
    scope.reportingAgentEnabled,
    scope.reportingEnabled,
  ])

  useEffect(
    () => () => {
      const current = scopeRef.current
      scrubReportingPrivateCache(
        queryClient,
        current.actorId,
        current.organizationId,
      )
    },
    [queryClient],
  )

  return null
}

interface ReportingAgentContentProps {
  enabled: boolean
  onAccessDenied: (event: ReportingAccessDeniedEvent) => void
  children: (
    onCreateReport: () => void,
    onAskQuestion: AskReportingChartQuestion,
  ) => React.ReactNode
}

const ReportingAgentContent: React.FC<ReportingAgentContentProps> = ({
  children,
  enabled,
  onAccessDenied,
}) => {
  const history = useHistory()
  const flyout = useFlyoutRoute()
  const { data: user } = useQueryOwnUser({})
  const reportingEnabled = useReportingAccess()
  const reportingAgentFlyoutOpen = flyout.name === 'reportingAgent'
  const [chartQuestionRequest, setChartQuestionRequest] =
    useState<ReportingChartQuestionRequest>()
  const onCreateReport = useCallback(() => {
    setChartQuestionRequest(undefined)
    flyout.open('reportingAgent', {
      draftRequestId: newDraftRequestId(),
    })
  }, [flyout])
  const onAskQuestion = useCallback<AskReportingChartQuestion>(
    (context, question) => {
      const id = newDraftRequestId()
      setChartQuestionRequest({ id, context, question })
      if (reportingAgentFlyoutOpen && flyout.params.conversationId) return
      flyout.open('reportingAgent', { draftRequestId: id })
    },
    [flyout, reportingAgentFlyoutOpen],
  )
  const onConversationAccepted = useCallback(
    (conversationId: string) =>
      history.replace(
        flyout.getOpenLocation('reportingAgent', { conversationId }),
      ),
    [flyout, history],
  )
  const closeReportingAgent = useCallback(() => {
    setChartQuestionRequest(undefined)
    flyout.close()
  }, [flyout])

  useEffect(() => {
    if (!enabled && reportingAgentFlyoutOpen) closeReportingAgent()
  }, [closeReportingAgent, enabled, reportingAgentFlyoutOpen])

  return (
    <>
      {children(onCreateReport, onAskQuestion)}
      {enabled &&
        reportingAgentFlyoutOpen &&
        user?.id &&
        user.organizationId && (
          <ReportingAgentFlyout
            actorId={user.id}
            organizationId={user.organizationId}
            draftRequestId={flyout.params.draftRequestId}
            conversationId={flyout.params.conversationId}
            chartQuestionRequest={chartQuestionRequest}
            isOpen
            reportingEnabled={reportingEnabled}
            onClose={closeReportingAgent}
            onConversationAccepted={onConversationAccepted}
            onAccessDenied={onAccessDenied}
          />
        )}
    </>
  )
}

interface ReportingFamilyContentProps {
  onAgentAccessDenied: (event: ReportingAccessDeniedEvent) => void
  onPinnedAccessDenied: () => void
}

const ReportingFamilyContent: React.FC<ReportingFamilyContentProps> = ({
  onAgentAccessDenied,
  onPinnedAccessDenied,
}) => {
  const translate = useTranslate()
  const history = useHistory()
  const location = useLocation()
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const { data: user } = useQueryOwnUser({})
  const reportingAgentEnabled = isReportingAgentEnabled(
    remoteConfig,
    user?.organizationId,
  )
  const subnavOptions = useMemo<SubnavOption[]>(
    () =>
      reportingRouteTree.family.tabs
        .filter((tab) => tab.key !== 'pinned' || reportingAgentEnabled)
        .map((tab) => ({
          label: translate(tab.labelSource),
          to: tab.path,
          ariaLabelSource: tab.labelSource,
          isActive: location.pathname === tab.path,
          'data-testid': `Reporting${
            tab.key === 'overview' ? 'Overview' : 'Pinned'
          }`,
        })),
    [location.pathname, reportingAgentEnabled, translate],
  )
  const overviewDateRange = useMemo(
    () =>
      createDateRangeFromSearch(location.search) ?? createDefaultDateRange(),
    [location.search],
  )
  const updateOverviewDateRange = useCallback(
    (range: ReportingDateRange) => {
      const search = new URLSearchParams(location.search)
      search.set('fromDate', range.since.format('YYYY-MM-DD'))
      search.set('toDate', range.until.format('YYYY-MM-DD'))
      history.replace({
        pathname: location.pathname,
        search: `?${search.toString()}`,
      })
    },
    [history, location.pathname, location.search],
  )
  const navigateFromOverview = useCallback(
    (destination: string) => history.push(destination),
    [history],
  )
  const resolveOverviewGatedDestination = useCallback(
    (
      type: PanelGatedState['type'],
      organizationId: string | undefined,
    ): string => {
      if (type === 'feature_disabled' || type === 'not_configured') {
        return departmentsUrl(organizationId ?? '')
      }
      if (type === 'not_connected') {
        return organizationIntegrationsUrl(organizationId ?? '')
      }
      return upgradeSubscriptionUrl()
    },
    [],
  )

  return (
    <>
      <ReportingPrivateCacheBoundary
        reportingAgentEnabled={reportingAgentEnabled}
      />
      <ReportingAgentContent
        enabled={reportingAgentEnabled}
        onAccessDenied={onAgentAccessDenied}
      >
        {(onCreateReport, onAskQuestion) => (
          <LayoutShell
            subnavOptions={subnavOptions}
            subnavDesign={SubnavDesign.Floating}
            title={translate('Reporting')}
            subtitle={translate(
              'Review company spend across accounts, teams, merchants, and people.',
            )}
            buttons={
              reportingAgentEnabled
                ? [
                    {
                      design: ButtonDesigns.designCta,
                      type: ButtonTypes.button,
                      text: translate('Create Report'),
                      image: (
                        <AiStarsIconV2
                          fill={cta_buttonPrimaryText}
                          width={16}
                          height={16}
                        />
                      ),
                      onClick: onCreateReport,
                      'data-testid': 'ReportingCreateReport',
                    },
                  ]
                : []
            }
            content={
              <Switch location={location}>
                <Route exact path={reportingPinnedUrl()}>
                  {reportingAgentEnabled ? (
                    <ReportingPinnedPage
                      embedded
                      onCreateReport={onCreateReport}
                      onAskQuestion={onAskQuestion}
                      onAccessDenied={onPinnedAccessDenied}
                    />
                  ) : (
                    <Redirect to={reportingRouteTree.family.rootPath} />
                  )}
                </Route>
                <Route exact path={reportingRouteTree.family.rootPath}>
                  <Reporting
                    embedded
                    onCreateReport={onCreateReport}
                    onAskQuestion={
                      reportingAgentEnabled ? onAskQuestion : undefined
                    }
                    initialDateRange={overviewDateRange}
                    onDateRangeChange={updateOverviewDateRange}
                    onNavigate={navigateFromOverview}
                    resolveGatedDestination={resolveOverviewGatedDestination}
                  />
                </Route>
              </Switch>
            }
          />
        )}
      </ReportingAgentContent>
    </>
  )
}

const ReportingFamilyPage: React.FC = () => {
  const flyout = useFlyoutRoute()
  const { data: user } = useQueryOwnUser({})
  const [transportAccessDenied, setTransportAccessDenied] = useState(false)
  const scopeRef = React.useRef({
    actorId: user?.id,
    organizationId: user?.organizationId,
  })
  const closeReportingFlyout = useCallback(() => {
    if (flyout.name === 'reportingAgent') flyout.close()
  }, [flyout])
  const revokeReportingTransportAccess = useCallback(() => {
    setTransportAccessDenied(true)
    closeReportingFlyout()
  }, [closeReportingFlyout])
  const handleAgentAccessDenied = useCallback(
    (event: ReportingAccessDeniedEvent) => {
      closeReportingFlyout()
      if (
        event.reason === 'transport' &&
        (event.status === 401 || event.status === 403)
      ) {
        setTransportAccessDenied(true)
      }
    },
    [closeReportingFlyout],
  )

  useEffect(() => {
    const previous = scopeRef.current
    if (
      previous.actorId !== user?.id ||
      previous.organizationId !== user?.organizationId
    ) {
      setTransportAccessDenied(false)
    }
    scopeRef.current = {
      actorId: user?.id,
      organizationId: user?.organizationId,
    }
  }, [user?.id, user?.organizationId])

  return (
    <ReportingAccessGate onAccessDenied={closeReportingFlyout}>
      {transportAccessDenied ? (
        <main data-testid="reporting-unavailable" />
      ) : (
        <ReportingFamilyContent
          onAgentAccessDenied={handleAgentAccessDenied}
          onPinnedAccessDenied={revokeReportingTransportAccess}
        />
      )}
    </ReportingAccessGate>
  )
}

export default ReportingFamilyPage
